import { useSwipeable } from 'react-swipeable'
import React, { useState, useEffect } from 'react'
import { useConfig } from 'msp-integrations'
import { RecommendationCarouselProps } from './RecommendationCarousel.types'
import {
  DefaultLeftIcon,
  DefaultRightIcon
} from './RecommendationCarousel.partials'
import * as S from './RecommendationCarousel.styles'

const TILE_SIZE = 338 // 324px + 14px spacing
const DESKTOP_MIN_WIDTH_PX = 1354
const iconStyles = {
  fill: '#000',
  stroke: '#000',
  strokeWidth: '10px',
  width: '24px'
}

/**
 * Render arrow icon for recommendation carousel
 */
function getArrowIcon(
  cbAction: () => void,
  shouldDisplay = false,
  icon = 'left'
): React.ReactNode {
  const icons = {
    left: (
      <DefaultLeftIcon
        fill={iconStyles?.fill}
        stroke={iconStyles?.stroke}
        strokeWidth={iconStyles?.strokeWidth}
        width={iconStyles?.width}
      />
    ),
    right: (
      <DefaultRightIcon
        fill={iconStyles?.fill}
        stroke={iconStyles?.stroke}
        strokeWidth={iconStyles?.strokeWidth}
        width={iconStyles?.width}
      />
    )
  }

  const cssClasses = [
    S.ArrowIconWrapper,
    !shouldDisplay ? 'opacity-0 pointer-events-none' : ''
  ]

  return (
    <div className={cssClasses.join(' ')} onClick={() => cbAction()}>
      {icons[icon] as React.ReactNode}
    </div>
  )
}

/**
 * Display Carousel slide status indicator
 */
function getIndicators(
  currentIndex: number,
  totalTiles: number,
  font: string
): JSX.Element {
  return (
    <div className={`${font} ${S.IndicatorWrapper}`}>
      {currentIndex} / {totalTiles}
    </div>
  )
}

const RecommendationCarousel = ({
  hideArrows = true,
  hideControl = true,
  hideIndicators = true,
  productTiles = []
}: RecommendationCarouselProps) => {
  const totalTiles = productTiles.length
  const [tileDisplayIndex, setTileDisplayIndex] = useState<number>(1)
  const [isDesktop, setIsDesktop] = useState<boolean>(false)
  const [showNextArrow, setShowNextArrow] = useState<boolean>(
    !hideArrows && totalTiles > 1
  )
  const [showPrevArrow, setShowPrevArrow] = useState<boolean>(
    !hideArrows && totalTiles > 1
  )

  const slideNext = () => {
    if (tileDisplayIndex + 1 <= totalTiles) {
      setTileDisplayIndex(tileDisplayIndex + 1)
    }
  }

  const slidePrev = () => {
    if (tileDisplayIndex - 1 > 0) {
      setTileDisplayIndex(tileDisplayIndex - 1)
    }
  }

  useEffect(() => {
    function handleResize() {
      if (window?.innerWidth >= DESKTOP_MIN_WIDTH_PX) setTileDisplayIndex(1)
      setIsDesktop(window?.innerWidth >= DESKTOP_MIN_WIDTH_PX)
    }

    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (tileDisplayIndex < totalTiles) {
      setShowNextArrow(!hideArrows && totalTiles > 1)
    } else {
      setShowNextArrow(false)
    }

    if (tileDisplayIndex <= 1) {
      setShowPrevArrow(false)
    } else {
      setShowPrevArrow(!hideArrows && totalTiles > 1)
    }
  }, [tileDisplayIndex])

  // Activate swipe for Mobile & Tablet only
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => slideNext(),
    onSwipedRight: () => slidePrev(),
    trackMouse: false,
    preventScrollOnSwipe: true
  })

  const {
    theme: { font }
  } = useConfig()

  return (
    <div className={S.RecommendationCarouselWrapper}>
      <div
        {...swipeHandlers}
        className={S.RecommendationCarouselTilesWrapper}
        style={{
          transform: `translateX(calc(${TILE_SIZE}px * ${-(
            tileDisplayIndex - 1
          )}))`
        }}
      >
        {totalTiles > 0 && productTiles.map((productTile) => productTile)}
      </div>
      {(!hideControl || !isDesktop) && totalTiles > 1 && (
        <div className={S.RecommendationCarouselControlsWrapper}>
          {getArrowIcon(slidePrev, showPrevArrow)}
          {(!hideIndicators || !isDesktop) &&
            getIndicators(tileDisplayIndex, totalTiles, font.medium)}
          {getArrowIcon(slideNext, showNextArrow, 'right')}
        </div>
      )}
    </div>
  )
}

export default RecommendationCarousel
